<template>
    <vx-expandable-card class="mb-4">
        <template v-slot:header>Pricing & Commitments</template>
        <template v-slot:content>
            <div class="flex flex-col gap-6 mt-3">
                <div v-if="!billingItemsLoading && billingItems.length > 0" class="flex flex-col gap-4">
                    <fieldset
                        class="inline-flex items-center justify-center gap-0.5 bg-gray-100 rounded-lg p-0.5"
                        data-test="month-tabs"
                    >
                        <legend class="sr-only">Billing month</legend>
                        <label
                            v-for="(item, index) in billingItems"
                            :key="index"
                            class="relative flex-1"
                            :data-test="`month-tab-${index === 0 ? 'previous' : index === 1 ? 'current' : 'next'}`"
                        >
                            <input
                                type="radio"
                                :value="index"
                                v-model="selectedMonth"
                                :name="billingMonthName"
                                :disabled="isEditingBillingItems"
                                class="peer sr-only"
                            />
                            <div
                                class="rounded-md px-4 py-2.5 text-center transition-all duration-200 text-gray-600 hover:text-gray-900 peer-checked:bg-white peer-checked:text-gray-900 peer-checked:shadow-sm peer-focus-visible:ring-2 peer-focus-visible:ring-blue-500 peer-disabled:opacity-50 peer-disabled:hover:text-gray-600"
                                :class="isEditingBillingItems ? 'cursor-not-allowed' : 'cursor-pointer'"
                            >
                                <div class="flex flex-col items-center gap-0.5">
                                    <span
                                        class="text-xs uppercase tracking-wider font-medium transition-colors"
                                        :class="index === 1 ? 'text-blue-600' : 'text-gray-600'"
                                    >
                                        {{ index === 0 ? 'Previous' : index === 1 ? 'Current' : 'Next' }}
                                    </span>
                                    <span class="text-sm font-semibold">
                                        {{ formatMonthYear(item.period) }}
                                    </span>
                                </div>
                            </div>
                        </label>
                    </fieldset>

                    <div v-if="team.status !== 'closed'" class="h-10 flex justify-between items-center">
                        <div
                            v-if="selectedMonth === 0 && !canEditPreviousPeriod() && currentBillingItem"
                            class="flex items-center gap-2 text-sm text-gray-500"
                        >
                            <svg
                                class="w-4 h-4"
                                fill="none"
                                stroke="currentColor"
                                viewBox="0 0 24 24"
                                aria-label="Lock icon"
                            >
                                <path
                                    stroke-linecap="round"
                                    stroke-linejoin="round"
                                    stroke-width="2"
                                    d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z"
                                ></path>
                            </svg>
                            <span>Historical rates cannot be modified</span>
                        </div>
                        <div v-else class="flex-1"></div>

                        <div v-if="(selectedMonth !== 0 || canEditPreviousPeriod()) && currentBillingItem">
                            <div v-if="!isEditingBillingItems">
                                <vx-button
                                    size="sm"
                                    color="primary"
                                    @click="startEditingBillingItems"
                                    data-test="edit-button"
                                >
                                    Adjust
                                </vx-button>
                            </div>
                            <div v-else class="flex gap-2">
                                <vx-button
                                    size="sm"
                                    color="primary"
                                    @click="saveBillingItems"
                                    :loading="savingBillingItems"
                                    data-test="save-button"
                                >
                                    Save Changes
                                </vx-button>
                                <vx-button
                                    size="sm"
                                    color="muted"
                                    @click="cancelEditingBillingItems"
                                    :disabled="savingBillingItems"
                                    data-test="cancel-button"
                                >
                                    Cancel
                                </vx-button>
                            </div>
                        </div>
                    </div>
                </div>

                <!-- Explanatory text for editing effects -->
                <div v-if="editingEffectDescription && team.status !== 'closed'" class="mt-3 mx-4">
                    <vx-alert color="warning" :closeable="false">
                        <template v-slot:icon>
                            <font-awesome-icon widthAuto :icon="faCircleInfo" />
                        </template>
                        {{ editingEffectDescription }}
                    </vx-alert>

                    <!-- Warning for parent teams about unit rates -->
                    <vx-alert v-if="isParent && isEditingBillingItems" color="info" :closeable="false" class="mt-2">
                        <template v-slot:icon>
                            <font-awesome-icon widthAuto :icon="faExclamationTriangle" />
                        </template>
                        Edits don't propagate to each sub-team. Take care with rate changes.
                    </vx-alert>
                </div>

                <div v-if="billingItemsLoading" class="py-3 text-center" data-test="pricing-loading">
                    <vx-icon-loading class="w-9 h-9" spin></vx-icon-loading>
                </div>
                <div v-else-if="billingItemsError">
                    <vx-alert color="danger" :closeable="false">
                        {{ billingItemsError }}
                    </vx-alert>
                </div>
                <div v-else-if="currentBillingItem" class="space-y-6">
                    <!-- Base Fees Section -->
                    <div>
                        <div class="text-xs font-medium text-gray-500 uppercase tracking-wider mb-2 px-2">
                            Base Fees
                        </div>
                        <div class="rounded-lg border border-gray-200 bg-white transition-all duration-200">
                            <div class="divide-y divide-gray-200">
                                <div
                                    class="flex justify-between items-center px-6"
                                    :class="isEditingBillingItems ? 'py-3' : 'py-4'"
                                >
                                    <span class="text-gray-700">License Fee</span>
                                    <div class="flex flex-col items-end">
                                        <span v-if="!isEditingBillingItems" class="font-mono text-gray-900">
                                            {{ formatMoney(currentBillingItem.license_fee) }}
                                        </span>
                                        <vx-input
                                            v-else
                                            v-model="editingValues.license_fee"
                                            type="number"
                                            :step="0.00001"
                                            :min="0"
                                            :max="1000000"
                                            size="sm"
                                            class="w-40! text-right font-mono"
                                            placeholder="0.00000"
                                            @blur="formatEditingValue('license_fee')"
                                            :error="billingItemsValidationErrors.license_fee"
                                            data-test="billing-item-input-base-license-fee"
                                        />
                                    </div>
                                </div>

                                <div
                                    class="flex justify-between items-center px-6"
                                    :class="isEditingBillingItems ? 'py-3' : 'py-4'"
                                >
                                    <span class="text-gray-700">Professional Services</span>
                                    <div class="flex flex-col items-end">
                                        <span v-if="!isEditingBillingItems" class="font-mono text-gray-900">
                                            {{ formatMoney(currentBillingItem.professional_services_fee) }}
                                        </span>
                                        <vx-input
                                            v-else
                                            v-model="editingValues.professional_services_fee"
                                            type="number"
                                            :step="0.00001"
                                            :min="0"
                                            :max="1000000"
                                            size="sm"
                                            class="w-40! text-right font-mono"
                                            placeholder="0.00000"
                                            @blur="formatEditingValue('professional_services_fee')"
                                            :error="billingItemsValidationErrors.professional_services_fee"
                                            data-test="billing-item-input-base-professional-services-fee"
                                        />
                                    </div>
                                </div>

                                <div
                                    class="flex justify-between items-center px-6"
                                    :class="isEditingBillingItems ? 'py-3' : 'py-4'"
                                >
                                    <span class="text-gray-700">Additional Seats</span>
                                    <span v-if="!isEditingBillingItems" class="font-mono text-gray-900">
                                        {{
                                            formatQuantityBasedFee(
                                                currentBillingItem.additional_seats_quantity,
                                                currentBillingItem.additional_seats_rate
                                            )
                                        }}
                                    </span>
                                    <!-- Edit mode -->
                                    <div v-else class="flex gap-2">
                                        <div class="flex flex-col items-end">
                                            <vx-input
                                                v-model="editingValues.additional_seats_quantity"
                                                type="number"
                                                :min="0"
                                                :max="10000"
                                                size="sm"
                                                class="text-right font-mono w-24!"
                                                placeholder="Quantity"
                                                :error="billingItemsValidationErrors.additional_seats_quantity"
                                                data-test="billing-item-input-additional-seats-quantity"
                                            />
                                        </div>
                                        <div class="flex flex-col items-end">
                                            <vx-input
                                                v-model="editingValues.additional_seats_rate"
                                                type="number"
                                                :step="0.00001"
                                                :min="0"
                                                :max="1000000"
                                                size="sm"
                                                class="w-40! text-right font-mono"
                                                placeholder="Rate"
                                                @blur="formatEditingValue('additional_seats_rate')"
                                                :error="billingItemsValidationErrors.additional_seats_rate"
                                                data-test="billing-item-input-additional-seats-rate"
                                            />
                                        </div>
                                    </div>
                                </div>

                                <div
                                    class="flex justify-between items-center px-6"
                                    :class="isEditingBillingItems ? 'py-3' : 'py-4'"
                                >
                                    <span class="text-gray-700">Location Fee</span>
                                    <span v-if="!isEditingBillingItems" class="font-mono text-gray-900">
                                        {{
                                            formatQuantityBasedFee(
                                                currentBillingItem.location_fee_quantity,
                                                currentBillingItem.location_fee_rate
                                            )
                                        }}
                                    </span>
                                    <!-- Edit mode -->
                                    <div v-else class="flex gap-2">
                                        <div class="flex flex-col items-end">
                                            <vx-input
                                                v-model="editingValues.location_fee_quantity"
                                                type="number"
                                                :min="0"
                                                :max="10000"
                                                size="sm"
                                                class="text-right font-mono w-24!"
                                                placeholder="Quantity"
                                                :error="billingItemsValidationErrors.location_fee_quantity"
                                                data-test="billing-item-input-location-fee-quantity"
                                            />
                                        </div>
                                        <div class="flex flex-col items-end">
                                            <vx-input
                                                v-model="editingValues.location_fee_rate"
                                                type="number"
                                                :step="0.00001"
                                                :min="0"
                                                :max="1000000"
                                                size="sm"
                                                class="w-40! text-right font-mono"
                                                placeholder="Rate"
                                                @blur="formatEditingValue('location_fee_rate')"
                                                :error="billingItemsValidationErrors.location_fee_rate"
                                                data-test="billing-item-input-location-fee-rate"
                                            />
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>

                    <!-- Unit Rates Section -->
                    <div>
                        <div class="text-xs font-medium text-gray-500 uppercase tracking-wider mb-2 px-2">
                            Unit Rates
                        </div>
                        <div class="rounded-lg border border-gray-200 bg-white transition-all duration-200">
                            <div class="divide-y divide-gray-200">
                                <div
                                    class="flex justify-between items-center px-6"
                                    :class="isEditingBillingItems ? 'py-3' : 'py-4'"
                                >
                                    <span class="text-gray-700">SMS Segment Unit Rate</span>
                                    <div class="flex flex-col items-end">
                                        <span v-if="!isEditingBillingItems" class="font-mono text-gray-900">
                                            {{ formatMoney(currentBillingItem.sms_segment_unit_rate) }}
                                        </span>
                                        <vx-input
                                            v-else
                                            v-model="editingValues.sms_segment_unit_rate"
                                            type="number"
                                            :step="0.00001"
                                            :min="0"
                                            :max="1000000"
                                            size="sm"
                                            class="w-40! text-right font-mono"
                                            placeholder="0.00000"
                                            @blur="formatEditingValue('sms_segment_unit_rate')"
                                            :error="billingItemsValidationErrors.sms_segment_unit_rate"
                                            data-test="billing-item-input-sms-segment-unit-rate"
                                        />
                                    </div>
                                </div>

                                <div
                                    class="flex justify-between items-center px-6"
                                    :class="isEditingBillingItems ? 'py-3' : 'py-4'"
                                >
                                    <span class="text-gray-700">MMS Message Unit Rate</span>
                                    <div class="flex flex-col items-end">
                                        <span v-if="!isEditingBillingItems" class="font-mono text-gray-900">
                                            {{ formatMoney(currentBillingItem.mms_message_unit_rate) }}
                                        </span>
                                        <vx-input
                                            v-else
                                            v-model="editingValues.mms_message_unit_rate"
                                            type="number"
                                            :step="0.00001"
                                            :min="0"
                                            :max="1000000"
                                            size="sm"
                                            class="w-40! text-right font-mono"
                                            placeholder="0.00000"
                                            @blur="formatEditingValue('mms_message_unit_rate')"
                                            :error="billingItemsValidationErrors.mms_message_unit_rate"
                                            data-test="billing-item-input-mms-message-unit-rate"
                                        />
                                    </div>
                                </div>

                                <div
                                    class="flex justify-between items-center px-6"
                                    :class="isEditingBillingItems ? 'py-3' : 'py-4'"
                                >
                                    <span class="text-gray-700">API Call Per Million Rate</span>
                                    <div class="flex flex-col items-end">
                                        <span v-if="!isEditingBillingItems" class="font-mono text-gray-900">
                                            {{ formatMoney(currentBillingItem.api_call_unit_rate) }}
                                        </span>
                                        <vx-input
                                            v-else
                                            v-model="editingValues.api_call_unit_rate"
                                            type="number"
                                            :step="0.00001"
                                            :min="0"
                                            :max="1000000"
                                            size="sm"
                                            class="w-40! text-right font-mono"
                                            placeholder="0.00000"
                                            @blur="formatEditingValue('api_call_unit_rate')"
                                            :error="billingItemsValidationErrors.api_call_unit_rate"
                                            data-test="billing-item-input-api-call-unit-rate"
                                        />
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>

                    <!-- Minimum Spend Commitment - Separate section -->
                    <div>
                        <div class="text-xs font-medium text-gray-500 uppercase tracking-wider mb-2 px-2">
                            Commitments
                        </div>
                        <div class="rounded-lg border border-gray-200 bg-white transition-all duration-200">
                            <div class="divide-y divide-gray-200">
                                <div
                                    class="flex justify-between items-center px-6"
                                    :class="isEditingBillingItems ? 'py-3' : 'py-4'"
                                >
                                    <div class="flex items-center gap-2">
                                        <span class="text-gray-700">Minimum Spend Commitment</span>
                                        <vx-badge
                                            v-if="!isEditingBillingItems && currentBillingItem.minimum_spend_commit > 0"
                                            size="xs"
                                            :color="
                                                currentBillingItem.minimum_spend_includes_base_fees
                                                    ? 'primary'
                                                    : 'amber'
                                            "
                                        >
                                            {{
                                                currentBillingItem.minimum_spend_includes_base_fees
                                                    ? 'Includes base'
                                                    : 'Usage only'
                                            }}
                                        </vx-badge>
                                    </div>
                                    <div class="flex flex-col items-end">
                                        <span v-if="!isEditingBillingItems" class="font-mono text-gray-900">
                                            {{ formatMoney(currentBillingItem.minimum_spend_commit) }}
                                        </span>
                                        <vx-input
                                            v-else
                                            v-model="editingValues.minimum_spend_commit"
                                            type="number"
                                            :step="0.00001"
                                            :min="0"
                                            :max="1000000"
                                            size="sm"
                                            class="w-40! text-right font-mono"
                                            placeholder="0.00000"
                                            @blur="formatEditingValue('minimum_spend_commit')"
                                            :error="billingItemsValidationErrors.minimum_spend_commit"
                                            data-test="billing-item-input-minimum-spend-commit"
                                        />
                                    </div>
                                </div>

                                <!-- Checkbox for base fees inclusion - show when editing -->
                                <div
                                    v-if="isEditingBillingItems"
                                    class="flex items-start gap-3 px-6 py-3 bg-gray-50 rounded-b-lg"
                                >
                                    <input
                                        v-model="editingValues.minimum_spend_includes_base_fees"
                                        type="checkbox"
                                        id="minimum-spend-includes-base"
                                        class="mt-1 w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                                        data-test="minimum-spend-includes-base-fees-checkbox"
                                    />
                                    <label for="minimum-spend-includes-base" class="flex-1">
                                        <div class="text-sm text-gray-700">
                                            Include base fees in minimum spend calculation
                                        </div>
                                        <div class="text-xs text-gray-500 mt-1">
                                            When checked, base fees (License, Professional Services, Additional Seats,
                                            Location) count toward the minimum spend. When unchecked, only usage fees
                                            apply.
                                        </div>
                                    </label>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </template>
    </vx-expandable-card>
</template>

<script setup>
import { ref, computed, onMounted, useId, watch } from 'vue';
import { VxButton, VxExpandableCard, VxInput, VxIconLoading, VxAlert, VxBadge } from '@voxie/frontend-components';
import { adminTeamsClient } from '~/services';
import { useToasted } from '~/composables/useToasted';
import { catchRequestError } from '~/utils/request';
import { useBillingDates } from '~/composables/useBillingDates';
import { faCircleInfo, faExclamationTriangle } from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome';
import { formatNumber } from '~/utils/helpers';

const props = defineProps({
    team: {
        type: Object,
        required: true,
    },
    teamId: {
        type: Number,
        required: true,
    },
});

const emit = defineEmits(['updated', 'unsavedChanges']);

const toasted = useToasted();
const { formatMonthYear, canEditPreviousPeriod } = useBillingDates();

const billingItems = ref([]);
const billingItemsLoading = ref(false);
const billingItemsError = ref(null);
const selectedMonth = ref(1);
const isEditingBillingItems = ref(false);
const editingValues = ref({});
const savingBillingItems = ref(false);
const billingItemsValidationErrors = ref({});

const billingMonthName = useId();

const currentBillingItem = computed(() => {
    return billingItems.value[selectedMonth.value] || null;
});

const isParent = computed(() => {
    return !props.team?.parent_id;
});

const editingEffectDescription = computed(() => {
    if (!isEditingBillingItems.value) {
        return '';
    }

    if (selectedMonth.value === 2) {
        return 'Changes will apply to this and all future months. Carrier fees are automatically applied.';
    }

    return 'Changes will apply to this and all future months, unless they have already been customized. Carrier fees are automatically applied.';
});

watch(isEditingBillingItems, (editing) => emit('unsavedChanges', editing));

const formatMoney = (dollars) => {
    const formatted = dollars.toFixed(5);
    const [integerPart, decimalPart] = formatted.split('.');
    const formattedInteger = integerPart.replace(/\B(?=(\d{3})+(?!\d))/g, ',');
    return '$' + formattedInteger + '.' + decimalPart;
};

const formatQuantityBasedFee = (quantity, rate) => {
    if (!quantity || !rate) {
        return '$0.00000';
    }
    return `${quantity} × $${parseFloat(rate).toFixed(5)}`;
};

const startEditingBillingItems = () => {
    if (!currentBillingItem.value) {
        return;
    }

    editingValues.value = {
        license_fee: currentBillingItem.value.license_fee.toFixed(5),
        professional_services_fee: (currentBillingItem.value.professional_services_fee || 0).toFixed(5),
        additional_seats_quantity: String(currentBillingItem.value.additional_seats_quantity || 0),
        additional_seats_rate: (currentBillingItem.value.additional_seats_rate || 0).toFixed(5),
        location_fee_quantity: String(currentBillingItem.value.location_fee_quantity || 0),
        location_fee_rate: (currentBillingItem.value.location_fee_rate || 0).toFixed(5),
        sms_segment_unit_rate: currentBillingItem.value.sms_segment_unit_rate.toFixed(5),
        mms_message_unit_rate: currentBillingItem.value.mms_message_unit_rate.toFixed(5),
        api_call_unit_rate: currentBillingItem.value.api_call_unit_rate.toFixed(5),
        minimum_spend_commit: currentBillingItem.value.minimum_spend_commit.toFixed(5),
        minimum_spend_includes_base_fees: currentBillingItem.value.minimum_spend_includes_base_fees ?? true,
    };

    billingItemsValidationErrors.value = {};
    isEditingBillingItems.value = true;
};

const cancelEditingBillingItems = () => {
    isEditingBillingItems.value = false;
    editingValues.value = {};
    billingItemsValidationErrors.value = {};
};

const formatEditingValue = (field) => {
    const value = parseFloat(editingValues.value[field]);
    if (!isNaN(value)) {
        editingValues.value[field] = value.toFixed(5);
    }
};

const validateQuantityField = (rawValue) => {
    const intValue = parseInt(rawValue);

    if (String(intValue) !== String(rawValue)) {
        return 'Quantity must be a whole number';
    } else if (isNaN(intValue)) {
        return 'Invalid number';
    } else if (intValue < 0) {
        return 'Cannot be negative';
    } else if (intValue > 10000) {
        return 'Cannot exceed 10,000';
    }
    return null;
};

const validateAmountField = (value) => {
    if (isNaN(value)) {
        return 'Invalid number';
    }

    if (value < 0) {
        return 'Cannot be negative';
    }

    if (value > 1000000) {
        return `Cannot exceed $${formatNumber(1000000)}`;
    }

    return null;
};

const validateBillingItems = () => {
    const errors = {};

    Object.keys(editingValues.value).forEach((field) => {
        if (field === 'minimum_spend_includes_base_fees') {
            return;
        } else if (field === 'additional_seats_quantity' || field === 'location_fee_quantity') {
            const error = validateQuantityField(editingValues.value[field]);
            if (error) {
                errors[field] = error;
            }
        } else {
            const value = parseFloat(editingValues.value[field]);
            const error = validateAmountField(value);
            if (error) {
                errors[field] = error;
            }
        }
    });

    billingItemsValidationErrors.value = errors;
    return Object.keys(errors).length === 0;
};

const saveBillingItems = async () => {
    if (savingBillingItems.value || !currentBillingItem.value) {
        return;
    }

    if (!validateBillingItems()) {
        return;
    }

    savingBillingItems.value = true;

    try {
        const data = {
            effective_date: currentBillingItem.value.period,
            license_fee: parseFloat(editingValues.value.license_fee),
            professional_services_fee: parseFloat(editingValues.value.professional_services_fee),
            additional_seats_quantity: parseInt(editingValues.value.additional_seats_quantity) || 0,
            additional_seats_rate: parseFloat(editingValues.value.additional_seats_rate),
            location_fee_quantity: parseInt(editingValues.value.location_fee_quantity) || 0,
            location_fee_rate: parseFloat(editingValues.value.location_fee_rate),
            sms_segment_unit_rate: parseFloat(editingValues.value.sms_segment_unit_rate),
            mms_message_unit_rate: parseFloat(editingValues.value.mms_message_unit_rate),
            api_call_unit_rate: parseFloat(editingValues.value.api_call_unit_rate),
            minimum_spend_commit: parseFloat(editingValues.value.minimum_spend_commit),
            minimum_spend_includes_base_fees: editingValues.value.minimum_spend_includes_base_fees ?? true,
        };

        await adminTeamsClient.setPricing(props.teamId, data);

        await fetchPricing(false);

        emit('updated');

        toasted.success('Pricing & commitments have been successfully updated');

        isEditingBillingItems.value = false;
        editingValues.value = {};
        billingItemsValidationErrors.value = {};
    } catch (e) {
        toasted.error(catchRequestError(e));
    } finally {
        savingBillingItems.value = false;
    }
};

const fetchPricing = async (showLoading = true) => {
    if (showLoading) {
        billingItemsLoading.value = true;
    }
    billingItemsError.value = null;

    try {
        const response = await adminTeamsClient.getPricing(props.teamId);
        billingItems.value = response?.data?.items || response?.items || [];
    } catch (e) {
        billingItemsError.value = 'Failed to load pricing';
        toasted.error(catchRequestError(e));
        console.error(e);
    } finally {
        if (showLoading) {
            billingItemsLoading.value = false;
        }
    }
};

onMounted(() => {
    fetchPricing();
});
</script>
